import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import ConfigContext from '../../../contexts/ConfigContext';
import usePageContent, { CmsRichText } from '../../../hooks/usePageContent';

// Components
import Meta from '../../shared/Meta';
import CostEstimator from '../content/CostEstimator';
import FeaturedTrucks from '../FeaturedTrucks';
import CtaImage from '../content/CtaImage';
import LogoList from '../content/LogoList';
import WhyBookWithUs from '../content/WhyBookWithUs';
import FeaturedCuisines from '../FeaturedCuisines';
import HowItWorks from '../content/HowItWorks';
import CateringCta from '../content/CateringCta';
import AnyEvent from '../content/AnyEvent';
import Testimonials from '../content/Testimonials';
import WeddingFaq from '../faqs/WeddingFaq';

import styles from '../../../../stylesheets/public/layouts/AboutPage.module.postcss';
import CDN_BASE, { cdnPhoto } from '../../../utils/cdn';

const getWeddingEvents = (vehicleType, cityName) => [
  {
    title: 'Reception',
    text: `Serve the main meal from a ${vehicleType}: tacos, wood-fired pizza, BBQ, or whatever fits the two of you. Guests order at the window, or the ${vehicleType} sets up a buffet line.`,
    icon: '/static/images/badges/weddingrings.svg'
  },
  {
    title: 'Cocktail hour',
    text: 'Small bites while guests mingle: sliders, bao, dumplings, or poke.',
    icon: '/static/images/badges/bubbles.svg'
  },
  {
    title: 'Rehearsal dinner',
    text: `A low-key dinner the night before. One ${vehicleType}, a long table, and no seating chart.`,
    icon: '/static/images/badges/most-booked.webp',
    width: 'lg'
  },
  {
    title: 'Late-night snacks',
    text: 'Tacos, grilled cheese, fries, or donuts for the dance-floor crowd before the night ends.',
    icon: '/static/images/badges/party.svg'
  },
  {
    title: 'Dessert',
    text: 'Ice cream, churros, cupcakes, or waffles, instead of the cake or alongside it.',
    icon: '/static/images/admin/catering/birthday.svg',
    width: 'sm'
  },
  {
    title: 'Day-after brunch',
    text: 'Breakfast burritos, coffee, and waffles for out-of-town guests before they head home.',
    icon: '/static/images/badges/truck.webp',
    width: 'lg'
  },
  {
    title: 'Elopements and micro weddings',
    text: `A small guest list still deserves good food. Ask ${vehicleType}s about minimums and shorter service windows.`,
    icon: '/static/images/badges/mask.svg'
  },
  {
    title: 'Almost any venue',
    text: `A ${vehicleType} brings its own kitchen, so it works at vineyards, ranches, parks, and backyards around ${cityName}. Check with your venue on parking and access.`,
    icon: '/static/images/badges/sft-favs.webp',
    width: 'lg'
  }
];

function WeddingCatering() {
  const { vehicle_type, city_name } = useContext(ConfigContext);
  const vehicleType = vehicle_type.toLowerCase();
  const citySlug = city_name.toLowerCase();
  const { content: cms } = usePageContent('catering_wedding');

  const weddingEvents = getWeddingEvents(vehicleType, city_name);

  return (
    <div className='CateringPage'>
      <Meta
        title={cms?.meta?.title || `Food ${vehicle_type} Wedding Catering in ${city_name}`}
        type='website'
        canonical='/food-truck-catering/wedding-catering'
        image={cdnPhoto(`${citySlug}-wedding-food-truck-catering`)}
        description={cms?.meta?.description || `Book food ${vehicleType} catering for a ${city_name} wedding. Receptions, rehearsal dinners, late-night snacks, and day-after brunch. Compare quotes before you book.`}
        structuredData={{
          cateringService: {
            name: `Food ${vehicle_type} Wedding Catering in ${city_name}`,
            description: `Book food ${vehicleType} wedding catering in ${city_name}. Receptions, rehearsal dinners, late-night snacks and more.`,
            url: '/food-truck-catering/wedding-catering',
            serviceType: 'Food Truck Wedding Catering',
          },
          breadcrumbs: {
            listItems: [
              { '@type': 'ListItem', position: 2, name: 'Catering', item: '/catering' },
              { '@type': 'ListItem', position: 3, name: 'Wedding Catering', item: '/food-truck-catering/wedding-catering' }
            ]
          }
        }}
      />

      <div className='Section'>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image={`${CDN_BASE}/photos/${citySlug}-wedding-food-truck-catering.webp`} alt='Food truck serving at a wedding reception' noLazy isResponsive />
          <div className={styles.Cta_copy}>
            <h1>{cms?.hero?.headline || `Food ${vehicleType} catering for ${city_name} weddings`}</h1>
            <CmsRichText
              html={cms?.hero?.subheadline}
              fallback={
                <p>Rehearsal dinners, receptions, and late-night service. Find {vehicleType}s that fit your venue, guest count, and style.</p>
              }
            />
            <p>
              <Link to='/catering/requests/new' className='Button Button--mobile-w100'>{cms?.hero?.cta_label || 'Get wedding catering quotes'}</Link>
            </p>
          </div>
        </div>
      </div>

      <section className='Section'>
        <LogoList titleClassName='u-textCenter' className='u-mt1' />
      </section>

      <section className='Section'>
        <div className={cn(styles.Cta, styles.Cta__list)}>
          <CtaImage image={`${CDN_BASE}/photos/${citySlug}-wedding-catering-custom-matches.webp`} alt='Wedding catering options from food trucks' isResponsive />
          <div className={styles.Cta_copy}>
            <div>
              <h3>Compare {vehicleType}s for your wedding</h3>
              <ul>
                <li>Requesting quotes is free.</li>
                <li>See menus and pricing before you choose.</li>
                <li>Book one {vehicleType} for the meal, or add a dessert or late-night {vehicleType}.</li>
              </ul>
            </div>
            <div>
              <h3>Talk to the {vehicleType}s directly</h3>
              <ul>
                <li>Message {vehicleType}s about menus, timing, and venue logistics.</li>
                <li>Keep event details editable while you compare options.</li>
                <li>Message Support if you have a question.</li>
              </ul>
            </div>
            <div>
              <h3>Book with no surprises</h3>
              <ul>
                <li>See the full price, including fees, before you book.</li>
                <li>Pay on the site once you&#39;ve chosen a {vehicleType}.</li>
                <li>Your quotes, messages, and booking details stay in one place.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <FeaturedTrucks />

      <WhyBookWithUs />

      <section className='Section--extraPadding'>
        <h2 className='u-textCenter u-mb3'>Planning food {vehicleType} service for a wedding</h2>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image={`${CDN_BASE}/photos/${citySlug}-wedding-food-truck-selection.webp`} alt='Selecting a food truck for a wedding' isResponsive />
          <div className={styles.Cta_copy}>
            <h4>Pick the menu</h4>
            <p>
              <Link to='/food-truck-catering/pizza-catering'>Wood-fired pizza</Link>, <Link to='/food-truck-catering/taco-catering'>tacos</Link>, <Link to='/food-truck-catering/asian-catering'>sushi and dumplings</Link>, BBQ, or a <Link to='/food-truck-catering/dessert-catering'>dessert {vehicleType}</Link> for later in the night. Many {vehicleType}s offer vegetarian, vegan, and gluten-free options; note what you need in your request so quotes reflect it. If you&#39;re working out a budget, <Link to='/food-truck-catering/prices'>see how catering is priced</Link> first.
            </p>
          </div>
        </div>
      </section>

      <section className='Section--extraPadding'>
        <div className={styles.Cta}>
          <CtaImage image={`${CDN_BASE}/photos/${citySlug}-wedding-catering-seamless-booking.webp`} alt='Booking a food truck for a wedding' isResponsive />
          <div className={styles.Cta_copy}>
            <h4>Plan for the venue</h4>
            <p>
              A {vehicleType} needs a flat spot about 30 ft by 8 ft, close enough to where guests gather. Ask your venue about parking, access, and whether a generator is allowed, then share the answers with the {vehicleType}s you&#39;re considering so their quotes account for it.
            </p>
          </div>
        </div>
      </section>

      <section className='Section--extraPadding'>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image={`${CDN_BASE}/photos/${citySlug}-wedding-catering-event-support.webp`} alt='Food truck team serving at a wedding' isResponsive />
          <div className={styles.Cta_copy}>
            <h4>Decide how many {vehicleType}s</h4>
            <p>
              One {vehicleType} usually covers a single service window. Ask each {vehicleType} how many guests it can serve per hour, then decide. For a larger guest list or a tight timeline, add a second {vehicleType}, or split the meal and dessert between two.
            </p>
          </div>
        </div>
      </section>

      <HowItWorks />

      <AnyEvent
        title={`Where a food ${vehicleType} fits in the wedding weekend`}
        events={weddingEvents}
      />

      <Testimonials />

      <CostEstimator />

      <section className='Section--extraPadding'>
        <FeaturedCuisines />
      </section>

      <section className='Section--extraPadding'>
        <WeddingFaq />
      </section>

      <section className='Section--extraPadding u-pt2'>
        <CateringCta />
      </section>

    </div>
  );
}

export default WeddingCatering;
